import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, Line } from "recharts";
import { Eye, Users, TrendingUp, ExternalLink } from "lucide-react";

const Stat = ({ label, value, sub, tid }) => (
  <div className="border hairline rounded-xl p-5 bg-white" data-testid={tid}>
    <div className="text-xs uppercase tracking-widest text-muted-foreground">{label}</div>
    <div className="font-serif text-3xl mt-1">{value.toLocaleString("en-IN")}</div>
    {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
  </div>
);

export default function AdminAnalytics() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    api.get("/admin/analytics/summary")
      .then(({ data }) => setD(data))
      .catch((e) => setErr(e.response?.data?.detail || "Could not load analytics"));
  }, []);

  if (err) return <div className="text-sm text-destructive">{err}</div>;
  if (!d) return <div className="text-sm text-muted-foreground">Loading analytics…</div>;

  const t = d.totals || {};
  return (
    <div className="space-y-6" data-testid="admin-analytics">
      <div>
        <div className="text-xs uppercase tracking-widest text-muted-foreground">Overview</div>
        <h1 className="font-serif text-3xl md:text-4xl">Traffic &amp; Visitors</h1>
        <p className="text-sm text-muted-foreground mt-1">Live from your storefront — updates automatically as customers browse.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <Stat tid="stat-today-views" label="Today · Page Views" value={t.today_views || 0} sub={`${t.today_uniques || 0} unique visitors`} />
        <Stat tid="stat-7d-views" label="7 Days · Views" value={t.d7_views || 0} sub={`${t.d7_uniques || 0} uniques`} />
        <Stat tid="stat-30d-views" label="30 Days · Views" value={t.d30_views || 0} sub={`${t.d30_uniques || 0} uniques`} />
      </div>

      <div className="border hairline rounded-xl p-5 bg-white">
        <div className="flex items-center gap-2 mb-3">
          <TrendingUp className="w-4 h-4 gold-text" strokeWidth={1.5} />
          <div className="text-sm font-medium">Views vs Unique Visitors — last 30 days</div>
        </div>
        <div style={{ height: 260 }}>
          <ResponsiveContainer>
            <AreaChart data={d.series || []} margin={{ top: 10, right: 20, bottom: 0, left: 0 }}>
              <defs>
                <linearGradient id="gViews" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#B8860B" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#B8860B" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#E6DFD3" />
              <XAxis dataKey="date" stroke="#786554" fontSize={10} tickFormatter={(v) => (v || "").slice(5)} />
              <YAxis stroke="#786554" fontSize={11} allowDecimals={false} />
              <Tooltip />
              <Area type="monotone" dataKey="views" stroke="#B8860B" strokeWidth={2} fill="url(#gViews)" />
              <Line type="monotone" dataKey="uniques" stroke="#8B5A2B" strokeWidth={2} dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div className="mt-2 flex items-center gap-4 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1"><span className="w-3 h-2 rounded-sm bg-[#B8860B]" /> Views</span>
          <span className="inline-flex items-center gap-1"><span className="w-3 h-2 rounded-sm bg-[#8B5A2B]" /> Unique visitors</span>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="border hairline rounded-xl p-5 bg-white" data-testid="top-paths">
          <div className="flex items-center gap-2 mb-3">
            <Eye className="w-4 h-4 gold-text" strokeWidth={1.5} />
            <div className="text-sm font-medium">Top Pages (last 7 days)</div>
          </div>
          <div className="divide-y hairline">
            {(d.top_paths || []).map((p, i) => (
              <div key={p.path + i} className="py-2 flex items-center justify-between text-sm gap-3">
                <a href={p.path} target="_blank" rel="noopener noreferrer" className="truncate hover:underline flex items-center gap-1 min-w-0">
                  <span className="truncate font-mono text-xs">{p.path}</span>
                  <ExternalLink className="w-3 h-3 shrink-0 text-muted-foreground" strokeWidth={1.5} />
                </a>
                <span className="text-xs font-medium tabular-nums">{p.views.toLocaleString("en-IN")}</span>
              </div>
            ))}
            {(d.top_paths || []).length === 0 && <div className="py-4 text-xs text-muted-foreground">No views yet.</div>}
          </div>
        </div>
        <div className="border hairline rounded-xl p-5 bg-white" data-testid="top-referrers">
          <div className="flex items-center gap-2 mb-3">
            <Users className="w-4 h-4 gold-text" strokeWidth={1.5} />
            <div className="text-sm font-medium">Top Referrers (last 7 days)</div>
          </div>
          <div className="divide-y hairline">
            {(d.top_referrers || []).map((r, i) => (
              <div key={r.referrer + i} className="py-2 flex items-center justify-between text-sm gap-3">
                <span className="truncate font-mono text-xs">{r.referrer}</span>
                <span className="text-xs font-medium tabular-nums">{r.views.toLocaleString("en-IN")}</span>
              </div>
            ))}
            {(d.top_referrers || []).length === 0 && <div className="py-4 text-xs text-muted-foreground">No referrers yet.</div>}
          </div>
        </div>
      </div>
    </div>
  );
}
